<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  columns: {
    type: Array,
    required: true
  },
  rows: {
    type: Array,
    required: true
  }
})

const emit = defineEmits(['stats-updated', 'filtered-rows-updated'])

// Number of games to show (starting from newest)
const gamesToShow = ref(0) // 0 means show all

// Opponent filter
const selectedOpponents = ref([])
const showOpponentDropdown = ref(false)
const opponentSearch = ref('')

// Define which columns should show totals (numeric columns)
const numericColumns = ['minutes', 'points', 'rebounds', 'assists', 'three_point_field_goal', 'steals', 'blocks', 'turnovers', 'pra', 'efficiency']

// Get unique opponents from all games
const availableOpponents = computed(() => {
  if (!props.rows || props.rows.length === 0) return []
  
  const opponents = [...new Set(props.rows.map(row => row.opponent_team_name || row.opponent).filter(Boolean))]
  return opponents.sort()
})

// Filtered opponents based on search
const filteredOpponents = computed(() => {
  if (!opponentSearch.value.trim()) {
    return availableOpponents.value
  }
  
  const searchTerm = opponentSearch.value.toLowerCase().trim()
  return availableOpponents.value.filter(opponent => 
    opponent.toLowerCase().includes(searchTerm)
  )
})

// Sort games by date (newest first) and limit based on gamesToShow and opponent filter
const filteredRows = computed(() => {
  if (!props.rows || props.rows.length === 0) return []
  
  let filtered = [...props.rows]
  
  // Filter by selected opponents
  if (selectedOpponents.value.length > 0) {
    filtered = filtered.filter(row => {
      const opponent = row.opponent_team_name || row.opponent
      return selectedOpponents.value.includes(opponent)
    })
  }
  
  // Sort by game_date (newest first)
  const sortedRows = filtered.sort((a, b) => {
    const dateA = new Date(a.game_date || a.datetime || 0)
    const dateB = new Date(b.game_date || b.datetime || 0)
    return dateB - dateA // Newest first
  })
  
  // Limit number of games if specified
  if (gamesToShow.value > 0) {
    return sortedRows.slice(0, gamesToShow.value)
  }
  
  return sortedRows
})

// Toggle opponent selection
const toggleOpponent = (opponent) => {
  const index = selectedOpponents.value.indexOf(opponent)
  if (index === -1) {
    selectedOpponents.value.push(opponent)
  } else {
    selectedOpponents.value.splice(index, 1)
  }
}

// Toggle all opponents
const toggleAllOpponents = () => {
  if (selectedOpponents.value.length === availableOpponents.value.length) {
    selectedOpponents.value = []
  } else {
    selectedOpponents.value = [...availableOpponents.value]
  }
}

// Check if all opponents are selected
const allOpponentsSelected = computed(() => {
  return selectedOpponents.value.length === availableOpponents.value.length && availableOpponents.value.length > 0
})

// Columns to skip (will be merged into score column)
const scoreColumns = ['home_team_score', 'away_team_score']

// Display columns - filters out individual score columns and adds merged score column
const displayColumns = computed(() => {
  const hasHomeScore = props.columns.some(col => col.key === 'home_team_score')
  const hasAwayScore = props.columns.some(col => col.key === 'away_team_score')
  
  // Filter out the individual score columns
  let cols = props.columns.filter(col => !scoreColumns.includes(col.key))
  
  // If both score columns existed, insert a merged "Score" column
  if (hasHomeScore && hasAwayScore) {
    // Find position to insert (after opponent_team_name or at end)
    const opponentIndex = cols.findIndex(col => col.key === 'opponent_team_name')
    const insertIndex = opponentIndex !== -1 ? opponentIndex + 1 : cols.length
    
    cols = [
      ...cols.slice(0, insertIndex),
      { key: 'merged_score', label: 'Score' },
      ...cols.slice(insertIndex)
    ]
  }
  
  return cols
})

// Helper to format merged score - bold the player's team score
const formatScore = (row) => {
  const homeScore = Number(row.home_team_score) || 0
  const awayScore = Number(row.away_team_score) || 0
  
  // Check if player's team is home or away
  const playerTeam = row.team || row.team_name || ''
  const homeTeam = row.home_team || row.home_team_name || ''
  const awayTeam = row.away_team || row.away_team_name || ''
  
  // Determine if player's team is home or away
  const isPlayerHome = playerTeam && homeTeam && playerTeam.toLowerCase() === homeTeam.toLowerCase()
  const isPlayerAway = playerTeam && awayTeam && playerTeam.toLowerCase() === awayTeam.toLowerCase()
  
  return { homeScore, awayScore, isPlayerHome, isPlayerAway }
}

// Calculate column totals based on filtered rows
const columnTotals = computed(() => {
  const totals = {}
  
  props.columns.forEach(col => {
    if (numericColumns.includes(col.key)) {
      // Sum numeric columns
      totals[col.key] = filteredRows.value.reduce((sum, row) => {
        return sum + (Number(row[col.key]) || 0)
      }, 0).toFixed(col.key === 'efficiency' ? 1 : 0)
    } else if (col.key === 'game_date') {
      // Show game count for date column
      totals[col.key] = `${filteredRows.value.length} games`
    } else {
      // Show dashes for non-numeric columns
      totals[col.key] = '—'
    }
  })
  
  return totals
})

// Calculate averages for specific columns based on filtered rows
const columnAverages = computed(() => {
  const averages = {}
  const gameCount = filteredRows.value.length
  
  if (gameCount === 0) return averages
  
  props.columns.forEach(col => {
    if (numericColumns.includes(col.key)) {
      const total = filteredRows.value.reduce((sum, row) => {
        return sum + (Number(row[col.key]) || 0)
      }, 0)
      
      if (col.key === 'efficiency') {
        // Show average for efficiency
        averages[col.key] = (total / gameCount).toFixed(1)
      } else {
        // Show per game average for other stats
        averages[col.key] = (total / gameCount).toFixed(1)
      }
    }
  })
  
  return averages
})

// Calculate filtered stats for projections (based on filtered games only)
const filteredStats = computed(() => {
  const gameCount = filteredRows.value.length
  
  if (gameCount === 0) {
    return {
      totalMinutes: 0,
      avgMinutes: 0,
      projections: {},
      per30Stats: {}
    }
  }
  
  // Calculate totals from filtered games
  const totals = {
    minutes: 0,
    points: 0,
    rebounds: 0,
    assists: 0,
    three_point_field_goal: 0,
    steals: 0,
    blocks: 0,
    turnovers: 0,
    pra: 0
  }
  
  filteredRows.value.forEach(row => {
    totals.minutes += Number(row.minutes) || 0
    totals.points += Number(row.points) || 0
    totals.rebounds += Number(row.rebounds) || 0
    totals.assists += Number(row.assists) || 0
    totals.three_point_field_goal += Number(row.three_point_field_goal) || 0
    totals.steals += Number(row.steals) || 0
    totals.blocks += Number(row.blocks) || 0
    totals.turnovers += Number(row.turnovers) || 0
    totals.pra += Number(row.pra) || 0
  })
  
  // Calculate averages per game
  const avgMinutes = totals.minutes / gameCount
  const avgPerGame = {}
  Object.keys(totals).forEach(key => {
    if (key !== 'minutes') {
      avgPerGame[key] = totals[key] / gameCount
    }
  })
  
  // Calculate per 30 minute stats (if player has average minutes)
  const per30Stats = {}
  if (avgMinutes > 0) {
    Object.keys(avgPerGame).forEach(key => {
      per30Stats[key] = (avgPerGame[key] / avgMinutes) * 30
    })
  }
  
  return {
    totalMinutes: totals.minutes,
    avgMinutes: avgMinutes,
    gameCount: gameCount,
    totals: totals,
    projections: avgPerGame, // Per game averages can be used as projections
    per30Stats: per30Stats
  }
})

// Emit updated stats whenever filtered data changes
watch(filteredStats, (newStats) => {
  emit('stats-updated', newStats)
}, { deep: true, immediate: true })

// Emit filtered rows whenever they change
watch(filteredRows, (newRows) => {
  emit('filtered-rows-updated', newRows)
}, { deep: true, immediate: true })
</script>

<template>
  <div class="flex-1 min-h-0 overflow-auto rounded-b-lg border border-gray-200 bg-white">
    <table class="w-max min-w-full text-xs md:text-sm text-left">
      <thead class="bg-gray-100 text-gray-700 sticky top-0 z-10">
        <tr>
          <!-- Index column with games limit input -->
          <th class="px-3 py-2 font-semibold whitespace-nowrap w-20">
            <div class="flex flex-col gap-1">
              <span class="text-xs">#</span>
              <input 
                v-model.number="gamesToShow"
                type="number"
                min="0"
                :max="props.rows.length"
                placeholder="All"
                class="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </th>
          <!-- Regular columns -->
          <th v-for="col in displayColumns" :key="col.key" class="px-3 py-2 font-semibold whitespace-nowrap relative">
            <!-- Special opponent column with filter -->
            <div v-if="col.key === 'opponent_team_name'" class="flex flex-col gap-1">
              <div class="flex items-center gap-1">
                <span class="text-xs">{{ col.label }}</span>
                <button 
                  @click="(e) => { e.stopPropagation(); showOpponentDropdown = !showOpponentDropdown; }"
                  @mousedown="(e) => e.stopPropagation()"
                  class="text-xs text-blue-600 hover:text-blue-800"
                >
                  <i class="fas fa-filter"></i>
                </button>
              </div>
              
              <!-- Selected opponents count -->
              <div class="text-xs text-gray-500">
                {{ selectedOpponents.length > 0 ? `${selectedOpponents.length} selected` : 'All' }}
              </div>
              
              <!-- Dropdown -->
              <div v-if="showOpponentDropdown" 
                   @click="(e) => e.stopPropagation()"
                   @mousedown="(e) => e.stopPropagation()"
                   @mouseup="(e) => e.stopPropagation()"
                   class="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 w-72">
                
                <!-- Search Input -->
                <div class="p-3 border-b border-gray-200">
                  <input 
                    v-model="opponentSearch"
                    type="text"
                    placeholder="Search opponents..."
                    @click="(e) => e.stopPropagation()"
                    @mousedown="(e) => e.stopPropagation()"
                    @mouseup="(e) => e.stopPropagation()"
                    @focus="(e) => e.stopPropagation()"
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <!-- Toggle All Button -->
                <div class="p-3 border-b border-gray-200">
                  <button
                    @click="(e) => { e.stopPropagation(); toggleAllOpponents(); }"
                    @mousedown="(e) => e.stopPropagation()"
                    @mouseup="(e) => e.stopPropagation()"
                    class="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200 hover:border-blue-300 transition-colors flex items-center justify-center gap-2"
                  >
                    <i class="fas" :class="allOpponentsSelected ? 'fa-check-square' : 'fa-square'"></i>
                    {{ allOpponentsSelected ? 'Deselect All' : 'Select All' }}
                  </button>
                </div>
                
                <!-- Opponent list with improved scrolling -->
                <div class="max-h-60 overflow-y-auto">
                  <div class="p-2">
                    <label 
                      v-for="opponent in filteredOpponents" 
                      :key="opponent" 
                      @click="(e) => e.stopPropagation()"
                      @mousedown="(e) => e.stopPropagation()"
                      @mouseup="(e) => e.stopPropagation()"
                      class="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                    >
                      <input 
                        type="checkbox"
                        :checked="selectedOpponents.includes(opponent)"
                        @change="(e) => { e.stopPropagation(); toggleOpponent(opponent); }"
                        @click="(e) => e.stopPropagation()"
                        @mousedown="(e) => e.stopPropagation()"
                        @mouseup="(e) => e.stopPropagation()"
                        class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span 
                        class="text-sm text-gray-700 flex-1 truncate select-none"
                        @click="(e) => { e.stopPropagation(); toggleOpponent(opponent); }"
                        @mousedown="(e) => e.stopPropagation()"
                        @mouseup="(e) => e.stopPropagation()"
                      >
                        {{ opponent }}
                      </span>
                    </label>
                    
                    <!-- No results message -->
                    <div v-if="filteredOpponents.length === 0 && opponentSearch.trim()" 
                         class="text-center text-gray-500 text-sm py-4"
                         @click="(e) => e.stopPropagation()"
                         @mousedown="(e) => e.stopPropagation()"
                         @mouseup="(e) => e.stopPropagation()">
                      No opponents found matching "{{ opponentSearch }}"
                    </div>
                  </div>
                </div>
                
                <!-- Footer with selection count -->
                <div 
                  class="p-3 border-t border-gray-200 bg-gray-50 rounded-b-lg"
                  @click="(e) => e.stopPropagation()"
                  @mousedown="(e) => e.stopPropagation()"
                  @mouseup="(e) => e.stopPropagation()">
                  <div class="text-xs text-gray-600 text-center">
                    {{ selectedOpponents.length }} of {{ availableOpponents.length }} opponents selected
                  </div>
                </div>
              </div>
            </div>
              
            <!-- Regular column headers -->
            <span v-else class="text-xs">{{ col.label }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in filteredRows" :key="row.id || idx" class="border-b hover:bg-gray-50">
          <!-- Index column -->
          <td class="px-3 py-2 whitespace-nowrap text-center font-medium text-gray-500">
            {{ idx + 1 }}
          </td>
          <!-- Regular columns -->
          <td v-for="col in displayColumns" :key="col.key" class="px-3 py-2 whitespace-nowrap">
            <!-- Merged score column with player's team score in bold -->
            <template v-if="col.key === 'merged_score'">
              <span :class="{ 'font-bold': formatScore(row).isPlayerHome }">{{ formatScore(row).homeScore }}</span>-<span :class="{ 'font-bold': formatScore(row).isPlayerAway }">{{ formatScore(row).awayScore }}</span>
            </template>
            <template v-else>{{ row[col.key] }}</template>
          </td>
        </tr>
      </tbody>
      <!-- Footer with totals -->
      <tfoot class="bg-blue-50 border-t-2 border-blue-200 sticky bottom-0">
        <!-- Totals Row -->
        <tr class="font-semibold text-blue-800">
          <!-- Index column total -->
          <td class="px-3 py-2 whitespace-nowrap text-center text-sm">
            {{ filteredRows.length }}
          </td>
          <!-- Regular column totals -->
          <td v-for="col in displayColumns" :key="`total-${col.key}`" class="px-3 py-2 whitespace-nowrap">
            <span v-if="col.key === 'game_date'" class="text-sm">{{ columnTotals[col.key] }}</span>
            <span v-else-if="numericColumns.includes(col.key)" class="text-sm">{{ columnTotals[col.key] }}</span>
            <span v-else class="text-gray-400">—</span>
          </td>
        </tr>
        <!-- Averages Row -->
        <tr class="font-medium text-blue-700 text-xs">
          <!-- Index column average -->
          <td class="px-3 py-1 whitespace-nowrap text-center text-xs text-gray-500">
            Avg
          </td>
          <!-- Regular column averages -->
          <td v-for="col in displayColumns" :key="`avg-${col.key}`" class="px-3 py-1 whitespace-nowrap">
            <span v-if="col.key === 'game_date'" class="text-xs text-gray-500">Per Game Avg</span>
            <span v-else-if="numericColumns.includes(col.key)" class="text-xs">{{ columnAverages[col.key] }}</span>
            <span v-else class="text-gray-400">—</span>
          </td>
        </tr>
      </tfoot>
    </table>
    
    <!-- Backdrop to close dropdown -->
    <div v-if="showOpponentDropdown" 
         @click="showOpponentDropdown = false" 
         @mousedown="showOpponentDropdown = false"
         @touchstart="showOpponentDropdown = false"
         class="fixed inset-0 z-20 bg-transparent"></div>
  </div>
</template> 